import React, { useContext } from "react";
import styled, { ThemeProps, withTheme } from "styled-components";
import { toFormattedNumber } from "../../../../../common/libs";
import {
  Centered,
  CircularProgress,
  Paper,
} from "../../../../../common/components/libs";

import {
  Button,
  Card as MuiCard,
  CardContent,
  Dialog,
  DialogActions,
  Theme,
  Typography,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";

import { Line } from "react-chartjs-2";
import PageStoreContext from "../context";
import { StatFormType } from "../store/graphforms";
import { observer } from "mobx-react";
import { useStores } from "../../../../../hooks";
import { StatsPeriodDataType } from "../../../../../services/stats/models";
import {Trans, useTranslation} from "react-i18next";

const Card = styled(MuiCard)(spacing);

const Spacer = styled.div(spacing);

const ChartWrapper = styled.div`
  height: 400px;
`;

function StatChart({ theme }: ThemeProps<Theme>) {
  const {i18n, t} = useTranslation(["general", "stats"]);
  const locale = i18n.language;
  const pageStore = useContext(PageStoreContext);
  const { constantsStore } = useStores();
  const { open } = pageStore.formsStore.forms[StatFormType.drawGraph];

  const { dataForGraph, graphTitle, salesIn, isLoadedGraphic, graphicType } =
    pageStore;

  let formatter;
  let dataForOy;
  if (pageStore.generalStats && constantsStore.isReady) {
    formatter = constantsStore.getCurrencyFormatter(
      pageStore.generalStats.totalOrdersPrice.currencyId
    );
    dataForOy = dataForGraph.map((data) => {
      if (
        graphicType === StatsPeriodDataType.averageCheck ||
        graphicType === StatsPeriodDataType.revenueAmount
      ) {
        return formatter.toShow(data.total, true);
      }
      return data.total;
    });
  }

  const dataForOx = dataForGraph.map((data) => data.time);

  const label = t("stats:sales");

  const data = {
    labels: dataForOx,
    datasets: [
      {
        label: `${label} (${salesIn})`,
        fill: true,
        backgroundColor: "transparent",
        borderColor: theme.palette.secondary.main,
        data: dataForOy,
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    tooltips: {
      intersect: false,
    },
    hover: {
      intersect: true,
    },
    plugins: {
      filler: {
        propagate: false,
      },
    },
    scales: {
      xAxes: [
        {
          reverse: true,
          gridLines: {
            color: "rgba(0,0,0,0.05)",
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            stepSize: 500,
            callback: function (value) {
              if (
                graphicType === StatsPeriodDataType.averageCheck ||
                graphicType === StatsPeriodDataType.revenueAmount
              ) {
                return toFormattedNumber(value);
              }
              return value;
            },
          },
          display: true,
          borderDash: [5, 5],
          gridLines: {
            color: "rgba(0,0,0,0)",
            fontColor: "#fff",
          },
        },
      ],
    },
  };

  const handleClose = () => {
    pageStore.formsStore.closeForm(StatFormType.drawGraph);
  };

  if (isLoadedGraphic) {
    return (
      <Dialog open={isLoadedGraphic} aria-labelledby="form-dialog-title">
        <Centered>
          <Paper
            mt={2}
            style={{
              minHeight: "200px",
              minWidth: "200px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress m={2} />
          </Paper>
        </Centered>
      </Dialog>
    );
  } else {
    return (
      <Dialog
        open={open}
        aria-labelledby="form-dialog-title"
        fullWidth={true}
        maxWidth={"md"}
        onClose={handleClose}
      >
        <Card mb={1}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t("stats:chart")} {graphTitle}
            </Typography>

            <Spacer mb={6} />

            <ChartWrapper>
              <Line data={data} options={options} />
            </ChartWrapper>
          </CardContent>
        </Card>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            {t("stats:close")}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default withTheme(observer(StatChart));
